import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, BoxProps } from '@joinrepublic/dls-ui-kit';
import { motion, useInView, useSpring } from 'framer-motion';

interface IMarqueeConfig {
  speed?: number;
  dragFactor?: number;
  touchDragFactor?: number;
  delay?: number;
}

interface IInteractiveMarqueeProps extends IMarqueeConfig {
  children: React.ReactNode;
  spacing?: BoxProps['padding'];
}

const MotionBox = motion(Box);

const isTouchDevice =
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

export const InteractiveMarquee = ({
  children,
  speed: initialSpeed = 0.9,
  dragFactor = 1.2,
  touchDragFactor = 2.5,
  spacing = 4,
  delay = 0,
}: IInteractiveMarqueeProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(wrapperRef, { once: false });
  const isDragClickRef = useRef<boolean>(false);
  const isDraggingRef = useRef<boolean>(false);

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [copies, setCopies] = useState<number>(2);

  const xRef = useRef<number>(0);
  const itemWidthRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);

  const speed = useSpring(initialSpeed, {
    damping: 30,
    stiffness: 120,
    mass: 1,
  });

  useEffect(() => {
    const animate = () => {
      const w = itemWidthRef.current;

      if (w > 0 && stripRef.current) {
        xRef.current -= speed.get();
        xRef.current = ((xRef.current % w) - w) % w;
        stripRef.current.style.transform = `translate3d(${
          xRef.current - w
        }px, 0, 0)`;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [speed]);

  useEffect(() => {
    if (!wrapperRef.current) return;

    const calculateCopies = () => {
      const firstChild = stripRef.current?.firstElementChild as HTMLElement;
      const contentWidth = firstChild?.getBoundingClientRect().width;

      if (!contentWidth) return;

      itemWidthRef.current = contentWidth;

      const neededCopies = Math.ceil(window.innerWidth / contentWidth) + 2;
      setCopies(Math.max(2, neededCopies));
    };

    const timer = setTimeout(calculateCopies, 0);
    window.addEventListener('resize', calculateCopies);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', calculateCopies);
    };
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (isInView) {
      timer = setTimeout(() => speed.set(initialSpeed), delay * 1000);
    } else {
      speed.set(0);
    }

    return () => clearTimeout(timer);
  }, [isInView, speed, initialSpeed, delay]);

  const onDragStart = useCallback(() => {
    isDraggingRef.current = true;
    setIsDragging(true);
    isDragClickRef.current = false;
    speed.set(0);
  }, [speed]);

  const onDrag = useCallback(
    (
      _e: MouseEvent | TouchEvent | PointerEvent,
      info: { delta: { x: number; y: number } }
    ) => {
      const deltaX = info.delta.x;

      if (Math.abs(deltaX) > 3 || Math.abs(info.delta.y) > 3) {
        isDragClickRef.current = true;
      }

      const factor = isTouchDevice ? touchDragFactor : dragFactor;
      speed.set(-deltaX * factor);
    },

    [speed, dragFactor, touchDragFactor]
  );

  const onDragEnd = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    speed.set(initialSpeed);

    setTimeout(() => {
      isDragClickRef.current = false;
    }, 100);
  }, [speed, initialSpeed]);

  const onMouseEnter = useCallback(() => {
    if (!isDraggingRef.current) speed.set(0);
  }, [speed]);

  const onMouseLeave = useCallback(() => {
    if (!isDraggingRef.current) speed.set(initialSpeed);
  }, [speed, initialSpeed]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const link = (e.target as HTMLElement).closest('a');
    if (link) e.preventDefault();
  }, []);

  const onClickCapture = useCallback((e: React.MouseEvent) => {
    const link = (e.target as HTMLElement).closest('a');

    if (isDragClickRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (link && !isDragClickRef.current) {
      e.preventDefault();
      e.stopPropagation();

      const href = link.getAttribute('href');
      const target = link.getAttribute('target');

      if (href) {
        if (target === '_blank') {
          window.open(href, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = href;
        }
      }
    }
  }, []);

  const renderChildren = () =>
    React.Children.toArray(children).map((child, i) => (
      <Box key={i} flex="0 0 auto" minW="max-content" pr={spacing}>
        {child}
      </Box>
    ));

  return (
    <MotionBox
      ref={wrapperRef}
      overflow="hidden"
      cursor={isDragging ? 'grabbing' : 'grab'}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.000001}
      dragMomentum={false}
      onDragStart={onDragStart}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onPointerDown={onPointerDown}
      onClickCapture={onClickCapture}
      sx={{
        img: { pointerEvents: 'none', userSelect: 'none' },
        touchAction: 'pan-y',
      }}
    >
      <Box
        ref={stripRef}
        display="flex"
        alignItems="start"
        userSelect="none"
        style={{ willChange: 'transform' }}
      >
        {Array.from({ length: copies }).map((_, index) => (
          <Box key={index} display="flex" flex="0 0 auto">
            {renderChildren()}
          </Box>
        ))}
      </Box>
    </MotionBox>
  );
};
